<template>
  <div id="nav-menu">
    <ul>
      <li
        v-for="item in menuItems"
        :key="item.name"
        @click="() => navigate(item)"
        :class="{
          active: item.active,
          'has-sub-items': navHasSubItems(item)
        }"
      >
        <div class="menu-wrapper">
          <font-awesome-icon class="icon" :icon="item.icon" />
          <span>{{$t(item.name)}}</span>

          <font-awesome-icon class="sub-indicator" :class="{ active: item.isExpanded }" :icon="faChevronDown" v-if="navHasSubItems(item)" />
        </div>

        <ul
          class="sub-menu"
          :class="{
            active: item.isExpanded,
            'has-one': (item.subItems.length === 1),
            'has-two': (item.subItems.length === 2),
            'has-three': (item.subItems.length === 3),
            'has-four': (item.subItems.length === 4)
          }"
          v-if="navHasSubItems(item)"
        >
          <li
            v-for="sub in item.subItems"
            :key="sub.name"
            @click="(e) => {
              e.stopPropagation();
              navigate(sub);
            }"
            :class="{ active: sub.active }"
          >
            <span>{{ $t(sub.name) }}</span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown, faEnvelope, faBriefcase, faStopwatch, faUsers, faUniversity, faCalculator, faBox } from '@fortawesome/free-solid-svg-icons';

import { hasPermissions } from '@/helpers/permission';
import { PATHS, PERMISSIONS } from '@/consts';

const { useState: usePhoneState } = createNamespacedHelpers('phone');

const items = [
  {
    name: 'ORDERS',
    permissions: [PERMISSIONS.ORDER_ACCESS],
    icon: faEnvelope,
    subItems: [
      {
        name: 'ALL_ORDERS',
        path: PATHS.ORDERS,
      },
      {
        name: 'MY_INBOX',
        path: PATHS.MY_ORDERS,
      }
    ]
  },
  {
    name: 'CONTRACTS',
    permissions: [PERMISSIONS.CONTRACT_ACCESS],
    path: PATHS.CONTRACTS,
    icon: faBriefcase
  },
  {
    name: 'PAYMENT_REMINDER',
    path: PATHS.PAYMENT_REMINDER,
    icon: faStopwatch,
    permissions: [PERMISSIONS.PAYMENT_REMINDER_ACCESS]
  },
  {
    name: 'VALUE_ADJUSTMENT',
    path: PATHS.VALUE_ADJUSTMENT,
    icon: faCalculator,
    permissions: [PERMISSIONS.VALUE_ADJUSTMENT_ACCESS]
  },
  {
    name: 'CLIENTS',
    path: PATHS.CLIENTS,
    icon: faUsers,
    permissions: [PERMISSIONS.CLIENT_ACCESS]
  },
  {
    name: 'ASSETS',
    path: PATHS.ASSETS,
    icon: faBox,
    permissions: [PERMISSIONS.ASSET_ACCESS]
  },
  {
    name: 'ACCOUNTANT',
    icon: faUniversity,
    permissions: [PERMISSIONS.CASH_LOG_ACCESS],
    subItems: [
      {
        name: 'CASH_LOG_MANAGEMENT',
        path: PATHS.CASH_LOG,
        permissions: [PERMISSIONS.CASH_LOG_ACCESS]
      }
    ]
  }
];

export default {
  name: 'NavMenu',
  components: {
    FontAwesomeIcon
  },
  setup(props, { root, emit }) {
    const { calling } = usePhoneState(['calling']);

    const menuItems = ref(items.filter((item) => hasPermissions(item.permissions)));

    const toggle = (nav, val) => {
      const i = menuItems.value.findIndex((item) => item.name === nav.name);
      const altItem = { ...menuItems.value[i] };
      altItem.isExpanded = (typeof val === 'boolean') ? val : !altItem.isExpanded;

      menuItems.value = [
        ...menuItems.value.slice(0, i),
        altItem,
        ...menuItems.value.slice(i + 1, menuItems.value.length)
      ];
    };

    const activate = (nav, sub) => {
      menuItems.value = menuItems.value.map((menuItem) => {
        const item = {
          ...menuItem,
          active: false
        };

        if (item.name === nav.name) {
          item.active = true;
        }

        if (item.subItems && item.subItems.length) {
          item.subItems.forEach((subItem, j) => {
            item.subItems[j].active = false;
            if (sub && (subItem.name === sub.name)) {
              item.subItems[j].active = true;
            }
          });
        }

        return item;
      });
    };

    const navHasSubItems = (nav) => (nav.subItems?.length);

    const navigate = (nav) => {
      if (navHasSubItems(nav)) {
        toggle(nav);
      } else if (nav.path) {
        root.$router.push(nav.path).catch(() => {});
        emit('clicked');
      } else if (nav.url) {
        let target = '_self';
        if (calling.value) {
          target = '_blank';
        }

        window.open(nav.url, target);
      }
    };

    const isNavActive = (nav) => {
      if (!nav.path) {
        if (nav.subItems && nav.subItems.length) {
          let sub;
          const flag = nav.subItems.some((item) => {
            if (isNavActive(item)) {
              sub = item;
              return true;
            }

            return false;
          });
          if (flag) {
            toggle(nav, true);
            activate(nav, sub);
          }

          return sub;
        }

        return false;
      }

      const { route } = root.$router.resolve(nav.path);
      const flag = (route.name === root.$route.name);
      if (flag) {
        activate(nav);
        return nav;
      }

      return false;
    };

    watch(
      () => root.$route,
      () => {
        menuItems.value.forEach((item) => isNavActive(item));
      }, {
        immediate: true
      }
    );

    return {
      faChevronDown,
      menuItems,
      navigate,
      navHasSubItems,
      toggle
    };
  }
};
</script>

<style lang="scss">
@import '~@/assets/scss/colors';

#nav-menu {
  width: 100%;

  @media (max-width: 992px) {
    padding-bottom: 40px;
  }

  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    width: 100%;

    & > li {
      padding: 15px 20px;
      color: $babyPowder;
      position: relative;
      cursor: pointer;
      transition: all .2s ease-in-out;

      .menu-wrapper {
        position: relative;
        z-index: 2;

        .icon {
          margin-right: 10px;
          width: 16px;
          vertical-align: middle;
        }

        span {
          vertical-align: middle;
          text-transform: uppercase;
          font-size: 14px;
        }

        .sub-indicator {
          position: absolute;
          top: 50%;
          right: 0;
          transform: translateY(-50%) rotate(-90deg);
          width: 12px;
          transition: all .2s ease-in-out;

          &.active {
            transform: translateY(-50%) rotate(0);
          }
        }
      }

      .sub-menu {
        width: calc(100% + 40px);
        height: 0;
        list-style: none;
        padding: 0;
        margin: 0;
        padding-left: 20px;
        margin-left: -20px;
        margin-top: 15px;
        margin-bottom: -10px;
        transition: all .2s ease-in-out;
        overflow: hidden;

        &.active {
          &.has-one {
            height: 54px;
          }

          &.has-two {
            height: 108px;
          }
        }

        & > li {
          padding: 15px 20px;
          position: relative;

          span {
            vertical-align: middle;
            text-transform: uppercase;
            font-size: 14px;
          }

          &:after {
            content: "";
            position: absolute;
            top: 0;
            left: -20px;
            width: 0;
            height: 100%;
            background-color: $babyPowder;
            transition: all .2s ease-in-out;
            z-index: 1;
          }

          &.active {
            color: $geranium;

            span {
              z-index: 2;
              position: inherit;
            }

            &:after {
              width: calc(100% + 20px);
              background-color: $babyPowder;
            }
          }
        }
      }

      &:before {
        content: "";
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 4px;
        height: 0;
        background-color: $babyPowder;
        transition: all .2s ease-in-out;
      }

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 0;
        height: 100%;
        background-color: $babyPowder;
        transition: all .2s ease-in-out;
        z-index: 1;
      }

      &:hover {
        &:before {
          height: 100%;
        }
      }

      &.active {
        color: $geranium;

        &:after {
          width: 100%;
        }

        &.has-sub-items {
          color: $babyPowder;

          &:before {
            height: 100%;
          }

          &:after {
            width: 0;
          }
        }
      }
    }
  }
}
</style>
